import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, MapPin, Phone, Loader2 } from 'lucide-react';
import axios from 'axios';
import { SEO, breadcrumbSchema } from '../components/SEO';
import { STATIC_SEO } from '../data/seoConfig';
import PageHero from '../components/PageHero';
import { Reveal } from '../components/Reveal';
import { SITE } from '../data/site';
import { SERVICES } from '../data/services';
import { LOCATIONS } from '../data/locations';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ContactPage = () => {
  const [form, setForm] = useState({ name: '', email: '', phone: '', address: '', city: '', projectType: '', scope: '', message: '', preferredContact: 'Phone', website: '' });
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    setError('');
    try {
      await axios.post(`${API}/contact`, {
        name: form.name, email: form.email, phone: form.phone,
        address: form.address, city: form.city, project_type: form.projectType,
        scope: form.scope, message: form.message, preferred_contact: form.preferredContact, website: form.website,
        page: window.location.pathname,
      });
      setStatus('sent');
    } catch (err) {
      setError('Something went wrong. Please try again or call us directly.');
      setStatus('idle');
    }
  };

  return (
    <>
      <SEO
        title={STATIC_SEO.contact.title}
        description={STATIC_SEO.contact.description}
        path="/contact/"
        jsonLd={[breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Contact', path: '/contact/' }])]}
      />
      <PageHero
        overline="Contact"
        title="Get in touch"
        intro="Ready to transform your outdoor space? Let's discuss your vision and create something extraordinary together."
        breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'Contact' }]}
        compact
      />
      <section className="py-20 md:py-24">
        <div className="container-site grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <h2 className="font-serif text-3xl font-medium tracking-tight text-ink">Let's create something amazing</h2>
              <p className="mt-5 text-base leading-relaxed text-muted2">
                Every outdoor space has unique potential. Our team of designers and craftspeople work closely with you to bring your vision to life, ensuring every detail reflects your style and enhances your lifestyle.
              </p>
              <div className="mt-10 space-y-6">
                <a href={`mailto:${SITE.email}`} data-testid="contact-email" className="flex items-center gap-4 border border-line p-5 transition-colors hover:border-bronze">
                  <Mail className="h-5 w-5 text-bronze" />
                  <span>
                    <span className="block text-xs font-semibold uppercase tracking-[0.2em] text-muted2">Email Us</span>
                    <span className="font-medium text-ink">{SITE.email}</span>
                  </span>
                </a>
                <a href={SITE.phoneHref} data-testid="contact-phone" className="flex items-center gap-4 border border-line p-5 transition-colors hover:border-bronze">
                  <Phone className="h-5 w-5 text-bronze" />
                  <span>
                    <span className="block text-xs font-semibold uppercase tracking-[0.2em] text-muted2">Call Us</span>
                    <span className="font-medium text-ink">{SITE.phone}</span>
                  </span>
                </a>
                <div className="flex items-center gap-4 border border-line p-5">
                  <MapPin className="h-5 w-5 text-bronze" />
                  <span>
                    <span className="block text-xs font-semibold uppercase tracking-[0.2em] text-muted2">Service Area</span>
                    <span className="font-medium text-ink">{SITE.serviceArea}</span>
                  </span>
                </div>
              </div>
              <div className="mt-10 border border-line bg-paper2/50 p-6">
                <h3 className="font-serif text-xl font-medium text-ink">What happens next?</h3>
                <ol className="mt-4 space-y-2.5 text-sm text-muted2">
                  {["We'll review your message", 'Schedule a consultation at your convenience', 'Create a custom design proposal', 'Begin transforming your outdoor space'].map((s, i) => (
                    <li key={s} className="flex gap-3"><span className="font-serif italic text-bronze">{i + 1}.</span> {s}</li>
                  ))}
                </ol>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              {status === 'sent' ? (
                <div data-testid="contact-success" className="border border-bronze/50 bg-paper2/50 p-10 text-center">
                  <h2 className="font-serif text-3xl font-medium text-ink">Message received</h2>
                  <p className="mx-auto mt-4 max-w-md text-muted2">Thank you — we've received your message and will be in touch shortly. Prefer to talk now? Call us at {SITE.phone}.</p>
                  <Link to="/projects/" data-testid="contact-success-work-link" className="btn-outline mt-8">Explore Our Work</Link>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="contact-form" className="border border-line bg-paper p-6 sm:p-10">
                  <h2 className="font-serif text-2xl font-medium text-ink">Request your free quote</h2>
                  <div className="mt-7 grid gap-5 sm:grid-cols-2">
                    <div>
                      <label htmlFor="c-name" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.2em] text-muted2">Name *</label>
                      <input id="c-name" data-testid="contact-name-input" required value={form.name} onChange={(e) => set('name', e.target.value)} className="input-field" autoComplete="name" />
                    </div>
                    <div>
                      <label htmlFor="c-phone" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.2em] text-muted2">Phone *</label>
                      <input id="c-phone" data-testid="contact-phone-input" type="tel" required value={form.phone} onChange={(e) => set('phone', e.target.value)} className="input-field" autoComplete="tel" />
                    </div>
                    <div>
                      <label htmlFor="c-email" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.2em] text-muted2">Email *</label>
                      <input id="c-email" data-testid="contact-email-input" type="email" required value={form.email} onChange={(e) => set('email', e.target.value)} className="input-field" autoComplete="email" />
                    </div>
                    <div>
                      <label htmlFor="c-city" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.2em] text-muted2">City</label>
                      <select id="c-city" data-testid="contact-city-select" value={form.city} onChange={(e) => set('city', e.target.value)} className="input-field">
                        <option value="">Select your city…</option>
                        {LOCATIONS.map((l) => <option key={l.slug} value={l.city}>{l.city}, NE</option>)}
                        <option value="Other">Other / nearby</option>
                      </select>
                    </div>
                    <div>
                      <label htmlFor="c-address" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.2em] text-muted2">Address (optional)</label>
                      <input id="c-address" data-testid="contact-address-input" value={form.address} onChange={(e) => set('address', e.target.value)} className="input-field" autoComplete="street-address" />
                    </div>
                    <div>
                      <label htmlFor="c-type" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.2em] text-muted2">Project type *</label>
                      <select id="c-type" data-testid="contact-type-select" required value={form.projectType} onChange={(e) => set('projectType', e.target.value)} className="input-field">
                        <option value="">Select a service…</option>
                        {SERVICES.map((s) => <option key={s.slug} value={s.navTitle}>{s.navTitle}</option>)}
                        <option value="Custom Design">Custom Design</option>
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label htmlFor="c-scope" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.2em] text-muted2">Estimated project scope</label>
                      <select id="c-scope" data-testid="contact-scope-select" value={form.scope} onChange={(e) => set('scope', e.target.value)} className="input-field">
                        <option value="">Select…</option>
                        <option>A single feature</option>
                        <option>A combination of features</option>
                        <option>A complete backyard transformation</option>
                        <option>Not sure yet</option>
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label htmlFor="c-message" className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.2em] text-muted2">Message *</label>
                      <textarea id="c-message" data-testid="contact-message-textarea" required rows={5} value={form.message} onChange={(e) => set('message', e.target.value)} className="input-field resize-none" placeholder="Tell us about your project…" />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="mb-1.5 block text-xs font-semibold uppercase tracking-[0.2em] text-muted2">Preferred contact method</label>
                      <div className="flex gap-2.5">
                        {['Phone', 'Email', 'Text'].map((m) => (
                          <button key={m} type="button" data-testid={`contact-method-${m.toLowerCase()}`} onClick={() => set('preferredContact', m)} aria-pressed={form.preferredContact === m}
                            className={`flex-1 border px-4 py-3 text-sm transition-colors ${form.preferredContact === m ? 'border-bronze bg-bronze/10 text-ink' : 'border-line text-ink/70'}`}>{m}</button>
                        ))}
                      </div>
                    </div>
                    <input type="text" name="website" value={form.website} onChange={(e) => set('website', e.target.value)} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
                  </div>
                  {error && <p data-testid="contact-form-error" className="mt-4 text-sm font-medium text-red-700">{error}</p>}
                  <button type="submit" data-testid="contact-submit-button" disabled={status === 'sending'} className="btn-primary mt-8 w-full disabled:opacity-50">
                    {status === 'sending' ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending…</> : 'Request Your Free Quote'}
                  </button>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
};

export default ContactPage;
